import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { api } from "../api/client";
import type { CatalogExercise } from "../api/types";
import { t, type MessageKey } from "../i18n";
import { typeLabel } from "../lib/activity";
import { useCatalog } from "../lib/data";
import CatalogExerciseSheet from "./CatalogExerciseSheet";
import ExerciseIcon from "./ExerciseIcon";
import { rankCatalog } from "./ExerciseNameLookup";

interface Props {
  query: string;
  onQuery: (q: string) => void;
  /** Catalog entry chosen (existing, quickly created, or created in the sheet). */
  onPick: (item: CatalogExercise) => void;
  /** Called with true while a catalog sheet is open (so Esc does not close the editor). */
  onSheetOpen?: (open: boolean) => void;
}

const PAGE = 50;

/** Full-height exercise search: input on top, big tappable results below. */
export default function ExercisePicker(props: Props) {
  const [catalog, { mutate }] = useCatalog();
  const [busy, setBusy] = createSignal(false);
  const [sheetOpen, setSheetOpen] = createSignal(false);

  const q = createMemo(() => props.query.trim());
  const results = createMemo(() => rankCatalog(catalog() ?? [], props.query, Infinity));
  // Render in pages as the list is scrolled: whole catalog reachable, few DOM rows at a time.
  const [shown, setShown] = createSignal(PAGE);
  const visible = createMemo(() => results().slice(0, shown()));
  createEffect(() => {
    void props.query;
    setShown(PAGE);
    if (listEl) listEl.scrollTop = 0;
  });
  let listEl: HTMLDivElement | undefined;
  let moreEl: HTMLDivElement | undefined;
  onMount(() => {
    if (typeof IntersectionObserver === "undefined" || !listEl || !moreEl) return;
    const io = new IntersectionObserver(
      (e) => e.some((x) => x.isIntersecting) && setShown((n) => (n >= results().length ? n : n + PAGE)),
      { root: listEl, rootMargin: "600px 0px" },
    );
    io.observe(moreEl);
    onCleanup(() => io.disconnect());
  });
  const exact = createMemo(() => {
    const low = q().toLowerCase();
    return low ? (catalog() ?? []).some((c) => c.name.trim().toLowerCase() === low) : false;
  });

  function remember(item: CatalogExercise) {
    const prev = catalog() ?? [];
    if (!prev.some((x) => x.id === item.id)) mutate([item, ...prev]);
  }

  function pick(item: CatalogExercise) {
    remember(item);
    props.onPick(item);
  }

  /** Get-or-create a strength catalog entry for the typed name. */
  async function createNamed() {
    const name = q();
    if (!name || busy()) return;
    setBusy(true);
    try {
      pick(await api.ensureCatalogExercise({ name }));
    } catch {
      /* offline / refused: stay on the list, the user can retry */
    } finally {
      setBusy(false);
    }
  }

  function setSheet(open: boolean) {
    setSheetOpen(open);
    props.onSheetOpen?.(open);
  }

  return (
    <div class="afs-picker">
      <div class="afs-search">
        <input
          class="input"
          type="text"
          inputmode="search"
          enterkeyhint="search"
          autocomplete="off"
          autocorrect="off"
          spellcheck={false}
          data-autofocus
          placeholder={t("FIND_EXERCISE")}
          aria-label={t("FIND_EXERCISE")}
          value={props.query}
          onInput={(e) => props.onQuery(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const first = results()[0];
            if (first) pick(first);
            else if (q()) void createNamed();
          }}
        />
      </div>

      <div class="afs-pick-list" role="listbox" ref={listEl}>
        <Show when={q() && !exact()}>
          <button
            type="button"
            class="afs-pick-row afs-pick-create"
            disabled={busy()}
            onClick={() => void createNamed()}
          >
            <span class="afs-pick-plus" aria-hidden="true">+</span>
            <span class="min-w-0 flex-1 truncate">
              {t("CREATE_NAMED").replace("{v}", q())}
            </span>
          </button>
        </Show>
        <For each={visible()}>
          {(item) => (
            <button type="button" role="option" class="afs-pick-row" onClick={() => pick(item)}>
              <ExerciseIcon icon={item.icon} type={item.exerciseType} class="text-[var(--accent)]" />
              <span class="min-w-0 flex-1">
                <span class="block truncate text-[17px] text-ink">{item.name}</span>
                <span class="block truncate text-[13px] text-ink-muted">
                  {[
                    typeLabel(item.exerciseType),
                    ...(item.muscleGroups ?? []).map((g) =>
                      t(`MG_${g.toUpperCase()}` as MessageKey),
                    ),
                  ].join(" · ")}
                </span>
              </span>
            </button>
          )}
        </For>
        <div class="afs-pick-more" ref={moreEl} aria-hidden="true" />
        <button type="button" class="afs-pick-row afs-pick-create" onClick={() => setSheet(true)}>
          <span class="afs-pick-plus" aria-hidden="true">+</span>
          <span class="min-w-0 flex-1 truncate">{t("CREATE_CUSTOM_ACTIVITY")}</span>
        </button>
      </div>

      <CatalogExerciseSheet
        open={sheetOpen()}
        initialName={q()}
        onClose={() => setSheet(false)}
        onSaved={(item) => {
          setSheet(false);
          pick(item);
        }}
      />
    </div>
  );
}
